import { useEffect, useState, useMemo, useCallback } from 'react';
import { FilterMatchMode } from 'primereact/api';
import { useCreatePreStockInspectionMutation, useGetAllPreStockInspectionQuery, useUpdatePreStockInspectionMutation } from '../../../../../api';
import { useGetDealerDropdownListQuery } from '../../../../../api/endpoints/dealer';
import toastService from '../../../../../utils/toastService';
import { createInitialFilters, convertFiltersToPrimeReactFormat } from '../../../../../utils/filterUtils';
import { useLanguage } from '../../../../../contexts/LanguageContext';

export const PSIStatusEnum = {
    DRAFT: 5,
    SENDTOKARSAN: 10
};

// Checklist question answer codes (used by the create/edit form and the table's OK/NOK column).
export const PSIAnswer = {
    OK: 1,
    NOK: 2
};

const usePreStockInspectionData = (currentStatus = null) => {
    const { th } = useLanguage();
    const [submitted, setSubmitted] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [apiFilters, setApiFilters] = useState({});

    // Dealer dropdown için API çağrısı
    const { data: dealerList = [] } = useGetDealerDropdownListQuery({
        RowCount: 100,
        SearchText: ''
    });

    // Transform dealer list for dropdown options
    const dealerOptions = useMemo(() => {
        return dealerList.map((dealer) => ({
            label: dealer.name,
            value: dealer.dealerId
        }));
    }, [dealerList]);

    // Durum enum'u UI için dropdown olarak
    const statusOptions = useMemo(
        () => [
            { label: th('warranty.preStockInspection.tabs.draft'), value: PSIStatusEnum.DRAFT },
            { label: th('warranty.preStockInspection.tabs.sent'), value: PSIStatusEnum.SENDTOKARSAN }
        ],
        [th]
    );

    const filterFields = useMemo(
        () => [
            { field: 'startDate', headerName: th('common.startDate'), type: 'date' },
            { field: 'endDate', headerName: th('common.endDate'), type: 'date' },
            { field: 'dealerId', headerName: th('common.dealer'), type: 'dropdown', options: dealerOptions },
            { field: 'vin', headerName: th('warranty.preStockInspection.filters.chassisNo.label'), placeholder: th('warranty.preStockInspection.filters.chassisNo.placeholder'), type: 'text' },
            { field: 'inspectorName', headerName: th('warranty.preStockInspection.filters.inspectorName.label'), placeholder: th('warranty.preStockInspection.filters.inspectorName.placeholder'), type: 'text' },
            { field: 'inspectorTitle', headerName: th('warranty.preStockInspection.filters.inspectorTitle.label'), placeholder: th('warranty.preStockInspection.filters.inspectorTitle.placeholder'), type: 'text' },
            { field: 'inspectionDate', headerName: th('warranty.preStockInspection.filters.inspectionDate.label'), placeholder: th('warranty.preStockInspection.filters.inspectionDate.placeholder'), type: 'date' },
            { field: 'stockInDate', headerName: th('warranty.preStockInspection.filters.stockInDate.label'), placeholder: th('warranty.preStockInspection.filters.stockInDate.placeholder'), type: 'date' },
            { field: 'status', headerName: th('warranty.preStockInspection.filters.status.label'), placeholder: th('warranty.preStockInspection.filters.status.placeholder'), type: 'select', options: statusOptions }
        ],
        [th, statusOptions, dealerOptions]
    );

    const filterTypeMap = useMemo(
        () => ({
            status: { type: 'number', matchMode: FilterMatchMode.EQUALS },
            inspectorName: { type: 'text', matchMode: FilterMatchMode.STARTS_WITH },
            inspectorTitle: { type: 'text', matchMode: FilterMatchMode.STARTS_WITH },
            vin: { type: 'text', matchMode: FilterMatchMode.STARTS_WITH },
            stockInDate: { type: 'date', matchMode: FilterMatchMode.DATE_AFTER },
            inspectionDate: { type: 'date', matchMode: FilterMatchMode.DATE_AFTER },
            startDate: { type: 'date', matchMode: FilterMatchMode.DATE_AFTER },
            endDate: { type: 'date', matchMode: FilterMatchMode.DATE_BEFORE },
            dealerId: { type: 'number', matchMode: FilterMatchMode.EQUALS }
        }),
        []
    );

    const filterFieldNames = useMemo(() => filterFields.map((f) => f.field), [filterFields]);
    const initialFilters = useMemo(() => createInitialFilters(filterFieldNames, filterTypeMap), [filterFieldNames, filterTypeMap]);
    const [filters, setFilters] = useState(initialFilters);

    const {
        data: apiData,
        isLoading,
        isFetching,
        error,
        refetch
    } = useGetAllPreStockInspectionQuery({
        pageSize: 100,
        pageNumber: 1,
        Status: currentStatus,
        ...apiFilters,
        ...(searchTerm ? { searchTerm } : {})
    });

    const apiPreStockInspection = useMemo(
    () => apiData?.preStockInsGetListQueryItems ?? [],
    [apiData]
    );

    const tabCounts = useMemo(() => {
     const map = {};
       (apiData?.statusCounts ?? []).forEach((i) => {
        map[i.status ?? 'ALL'] = i.count;
       });
       return map;
    }, [apiData]);


    const [createPreStockInspection, { isLoading: isCreating }] = useCreatePreStockInspectionMutation();
    const [updatePreStockInspection, { isLoading: isUpdating }] = useUpdatePreStockInspectionMutation();

    const savePreStockInspection = async (data, isEdit, isThereAnyChanges) => {
        try {
            if (isEdit && !isThereAnyChanges) {
                toastService.warn(th('warranty.preStockInspection.messages.noChangesWarning'));
                return true;
            }
            const response = isEdit ? await updatePreStockInspection(data).unwrap() : await createPreStockInspection(data).unwrap();

            if (!response.isSuccessful) {
                toastService.error(response.errorMessage || th('warranty.preStockInspection.messages.operationFailed'));
                return false;
            }
            toastService.success(isEdit ? th('warranty.preStockInspection.messages.updateSuccess') : th('warranty.preStockInspection.messages.createSuccess'));

            refetch();
            return true;
        } catch (error) {
            console.error('Error saving pre stock inspection:', error);
            toastService.error(th('warranty.preStockInspection.messages.saveError'));
            return false;
        }
    };

    const handleSearch = useCallback((value) => {
        setSearchTerm(value);
    }, []);

    const handleApplyFilters = useCallback(
        (updatedFilters) => {
            const convertedFilters = convertFiltersToPrimeReactFormat(updatedFilters, filters, filterTypeMap);
            setFilters(convertedFilters);

            const newApiFilters = {};
            Object.entries(updatedFilters).forEach(([key, value]) => {
                if (value !== null && value !== undefined && value !== '') {
                    if (key === 'startDate' || key === 'endDate') {
                        // Date alanları için özel işlem
                        const selectedDate = new Date(value);
                        const timezoneOffset = selectedDate.getTimezoneOffset();
                        const adjustedDate = new Date(selectedDate.getTime() - (timezoneOffset * 60000));
                        newApiFilters[key] = adjustedDate.toISOString();
                    }
                    else if (key === 'status') {
                        newApiFilters['FilterStatus'] = value;  
                    }
                     else {
                        const pascalKey = key.charAt(0).toUpperCase() + key.slice(1);
                        newApiFilters[pascalKey] = value instanceof Date ? value.toISOString() : value;
                    }
                }
            });

            setApiFilters(newApiFilters);
        },
        [filters, filterTypeMap]
    );

    const handleResetFilters = useCallback(() => {
        const reset = createInitialFilters(filterFieldNames, filterTypeMap);
        setFilters(reset);
        setApiFilters({});
        setSearchTerm('');
    }, [filterFieldNames, filterTypeMap]);

    return {
        submitted,
        filters,
        filterFields,
        apiPreStockInspection,
        tabCounts,
        isLoading,
        isFetching,
        error,
        isCreating,
        isUpdating,
        refetch,
        setSubmitted,
        savePreStockInspection,
        handleSearch,
        handleApplyFilters,
        handleResetFilters,
        dealerOptions,
        apiFilters,      
        currentStatus
    };
};

export default usePreStockInspectionData;
